import { useState, type ReactNode } from 'react';

export interface SelectableCardProps {
  selected: boolean;
  onSelect: () => void;
  /** Custom body content. When provided, icon/title/description are ignored. */
  children?: ReactNode;
  icon?: ReactNode;
  title?: string;
  description?: string;
  disabled?: boolean;
  /** role="radio" + aria-checked for single-select groups. Wrap list in div role="radiogroup". */
  role?: 'button' | 'radio';
}

export function SelectableCard({
  selected,
  onSelect,
  children,
  icon,
  title,
  description,
  disabled = false,
  role = 'button',
}: SelectableCardProps) {
  const [hovered, setHovered] = useState(false);

  const borderColor = selected
    ? 'var(--evo-cyan)'
    : hovered && !disabled
      ? 'var(--evo-steel)'
      : 'var(--evo-stroke)';

  const bg = selected ? 'rgba(0,212,255,0.06)' : 'var(--evo-bg-2)';

  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'radio' ? selected : undefined}
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--evo-space-3)',
        width: '100%',
        padding: 'var(--evo-space-3) var(--evo-space-4)',
        background: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: 'var(--evo-r-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 120ms linear, background 120ms linear',
        outline: 'none',
        boxSizing: 'border-box',
        fontFamily: 'var(--evo-font)',
      }}
    >
      {children != null ? children : (
        <>
          {icon != null && (
            <span style={{ display: 'flex', flexShrink: 0, marginTop: '1px' }}>
              {icon}
            </span>
          )}
          <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)', minWidth: 0 }}>
            <span style={{
              fontSize: 'var(--evo-fs-label)',
              fontWeight: 600,
              color: 'var(--evo-fg-1)',
              fontFamily: 'var(--evo-font)',
              lineHeight: 1.3,
            }}>
              {title}
            </span>
            {description != null && (
              <span style={{
                fontSize: 'var(--evo-fs-caption)',
                color: 'var(--evo-fg-3)',
                fontFamily: 'var(--evo-font)',
                lineHeight: 1.4,
              }}>
                {description}
              </span>
            )}
          </span>
        </>
      )}
    </button>
  );
}
